// Pesquisa de fotos — usado dentro do modal "Foto do produto" (botao "Pesquisar Fotos").
// Lista TODAS as fotos que o sistema ja tem (inclusive a do proprio produto em edicao)
// e permite buscar por nome do produto, codigo ou nome da foto. Clicar numa foto
// reaproveita a MESMA imagem aqui (zero arquivo novo armazenado). Cada foto tem nome
// proprio (editavel), tamanho em disco e pode ser excluida do acervo.
// Carrega tudo de uma vez e filtra localmente (busca instantanea).
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import {
  Search, ArrowLeft, Pencil, Trash2, Check, X, Upload, Scissors, BadgeCheck, CopyPlus, MoreVertical,
} from 'lucide-react';
import { api } from '../../lib/api';
import { useDialog } from '../../components/DialogProvider';
import { Modal } from '../../components/Modal';
import { BotaoRetirarFundo } from '../../components/BotaoRetirarFundo';
import { ModalAplicarFotoProdutos } from './ModalAplicarFotoProdutos';
import { ModalEnviarFotosAcervo } from './ModalEnviarFotosAcervo';

interface GaleriaFoto {
  foto_url: string;
  nome: string | null;
  // Versao sem fundo (transparente) da foto, quando ja foi recortada. Ver DA-072.
  master_url: string | null;
  // true quando o master atende a regua de qualidade completa (DA-073).
  master_completo: boolean;
  bytes: number;
  // Data do arquivo em disco — so ordena a lista no backend; a tela nao exibe.
  mtime?: number;
  // 0 = foto SOLTA: existe no disco e nenhum produto aponta pra ela. Desde 2026-08-07 isso pode ser
  // legitimo (foto promovida do Monitor, esperando vinculo) alem das sobras antigas.
  usos: number;
  produtos: { id: number; codigo_interno: string | null; descricao: string }[];
}

// Dimensoes + peso de uma foto, medidos no navegador.
interface MedidaFoto { largura: number; altura: number; bytes: number }

interface Props {
  // Clicar numa foto a reaproveita. Ausente = modo so-consulta (pagina Gestao de Fotos).
  onEscolher?: (fotoUrl: string) => void;
  // Botao "Voltar". Ausente = sem botao (quando embutido como aba de pagina).
  onCancelar?: () => void;
  // Avisa o formulario quando uma foto e excluida (pra limpar a foto do produto
  // atual caso fosse justamente essa).
  onFotoExcluida?: (fotoUrl: string) => void;
}

function kb(bytes: number): string {
  if (!bytes) return '—';
  return `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

// Item do menu "..." de acoes da foto.
function ItemMenu({
  icone,
  cor,
  texto,
  onClick,
}: {
  icone: ReactNode;
  cor: string;
  texto: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="hover:bg-black/5"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 9,
        width: '100%',
        textAlign: 'left',
        background: 'transparent',
        border: 'none',
        borderRadius: 6,
        padding: '8px 9px',
        cursor: 'pointer',
        fontSize: 13,
        color: 'var(--text-color)',
      }}
    >
      <span style={{ color: cor, display: 'flex', flexShrink: 0 }}>{icone}</span>
      {texto}
    </button>
  );
}

export function PickerFotoProduto({ onEscolher, onCancelar, onFotoExcluida }: Props) {
  const dialog = useDialog();
  const [galeria, setGaleria] = useState<GaleriaFoto[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [busca, setBusca] = useState('');
  // Oculta fotos que ja tem master (parcial ou completo) — mostra so o que falta.
  const [ocultarMaster, setOcultarMaster] = useState(false);
  // "Fotos sem Vinculo" (pedido do Ruan, 04/11/2026): so as fotos que nenhum produto usa, pra
  // ponderar a exclusao. Mesmo criterio do selo SEM PRODUTO e do contador (usos === 0).
  const [soSemVinculo, setSoSemVinculo] = useState(false);
  // Envio de fotos avulsas pro acervo (so no modo gestao).
  const [enviarAberto, setEnviarAberto] = useState(false);
  // Edicao de nome inline
  const [editandoUrl, setEditandoUrl] = useState<string | null>(null);
  const [nomeEdit, setNomeEdit] = useState('');
  const [salvandoNome, setSalvandoNome] = useState(false);
  const [excluindo, setExcluindo] = useState<string | null>(null);
  // Selecao pra apagar varias de uma vez (pedido do Ruan, 04/11/2026). Nasce VAZIA: a marcacao e'
  // a decisao, e a tela nao decide por ele. Chave = foto_url, que sobrevive a troca de filtro.
  const [selecionadas, setSelecionadas] = useState<Set<string>>(new Set());
  const [apagandoLote, setApagandoLote] = useState<{ feitas: number; total: number } | null>(null);
  // Substituir o arquivo de uma foto por outro (atualiza todos os produtos de uma vez).
  const [substituindo, setSubstituindo] = useState<string | null>(null);
  // Zoom: foto exibida ampliada em tela cheia (null = fechado).
  const [zoom, setZoom] = useState<string | null>(null);
  // Foto sendo aplicada em varios produtos (null = modal fechado).
  const [aplicarEmProdutos, setAplicarEmProdutos] = useState<GaleriaFoto | null>(null);
  // Menu "..." de acoes da foto (posicao fixed, ancorado no botao).
  const [menuFoto, setMenuFoto] = useState<
    { foto: GaleriaFoto; top: number; left: number } | null
  >(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  // Foto sendo recortada pelo BotaoRetirarFundo controlado (null = inativo).
  const [retirarFundoFoto, setRetirarFundoFoto] = useState<GaleriaFoto | null>(null);
  const fotoSubstituirRef = useRef<GaleriaFoto | null>(null);
  const inputSubstituirRef = useRef<HTMLInputElement | null>(null);
  // Foto recebendo um Master "pronto" (ja recortado fora, ex: FotoTool) — sem IA no navegador.
  const fotoMasterRef = useRef<GaleriaFoto | null>(null);
  const inputMasterRef = useRef<HTMLInputElement | null>(null);
  // Modal de comparacao "foto atual x foto nova" antes de confirmar a substituicao.
  const [modalSubst, setModalSubst] = useState<
    { foto: GaleriaFoto; urlNova: string; nomeNovo: string; atual: MedidaFoto; nova: MedidaFoto } | null
  >(null);
  const [aplicandoSubst, setAplicandoSubst] = useState(false);
  // Popover "quais produtos usam esta foto" — aberto pelo clique no "N produtos".
  const [listaAberta, setListaAberta] = useState<
    { foto: GaleriaFoto; top: number; left: number; acima: boolean } | null
  >(null);
  const popoverRef = useRef<HTMLDivElement | null>(null);

  function carregar() {
    setCarregando(true);
    api
      .get<GaleriaFoto[]>('/api/produtos/fotos-galeria')
      .then((r) => setGaleria(r))
      .catch(() => setGaleria([]))
      .finally(() => setCarregando(false));
  }
  useEffect(carregar, []);

  // Fecha o zoom da foto com Esc.
  useEffect(() => {
    if (!zoom) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setZoom(null);
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [zoom]);

  // Fecha o menu "..." com Esc ou clique fora.
  useEffect(() => {
    if (!menuFoto) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setMenuFoto(null);
    }
    function onClick(e: MouseEvent) {
      const alvo = e.target as HTMLElement;
      if (menuRef.current?.contains(alvo)) return;
      if (alvo.closest('[data-menu-foto]')) return; // o proprio gatilho cuida do toggle
      setMenuFoto(null);
    }
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onClick);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onClick);
    };
  }, [menuFoto]);

  // Abre/fecha o menu "..." ancorado no botao clicado. Flipa pra cima se nao cabe.
  function toggleMenu(e: React.MouseEvent, f: GaleriaFoto) {
    if (menuFoto?.foto.foto_url === f.foto_url) {
      setMenuFoto(null);
      return;
    }
    const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
    const alturaEstim = 280; // 6 itens
    const larguraMenu = 220;
    const acima = r.bottom + alturaEstim > window.innerHeight && r.top > alturaEstim;
    setMenuFoto({
      foto: f,
      top: acima ? r.top - alturaEstim - 4 : r.bottom + 4,
      left: Math.max(8, Math.min(r.right - larguraMenu, window.innerWidth - larguraMenu - 8)),
    });
  }

  // Fecha o popover de produtos com Esc ou clique fora.
  useEffect(() => {
    if (!listaAberta) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setListaAberta(null);
    }
    function onClick(e: MouseEvent) {
      const alvo = e.target as HTMLElement;
      if (popoverRef.current?.contains(alvo)) return;
      if (alvo.closest('[data-foto-usos]')) return; // o proprio gatilho cuida do toggle
      setListaAberta(null);
    }
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onClick);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onClick);
    };
  }, [listaAberta]);

  // Abre/fecha o popover ancorado no "N produtos" clicado. Flipa pra cima se nao cabe.
  function toggleLista(e: React.MouseEvent, f: GaleriaFoto) {
    if (listaAberta?.foto.foto_url === f.foto_url) {
      setListaAberta(null);
      return;
    }
    const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
    const alturaEstim = Math.min(320, 56 + f.produtos.length * 22);
    const acima = r.bottom + alturaEstim > window.innerHeight && r.top > alturaEstim;
    setListaAberta({
      foto: f,
      top: acima ? r.top - alturaEstim - 4 : r.bottom + 4,
      left: Math.min(r.left, window.innerWidth - 320),
      acima,
    });
  }

  // Busca local instantanea — nome da foto, codigo ou descricao dos produtos.
  // O filtro "Ocultar Master" remove fotos que ja tem master (parcial ou completo).
  const filtrados = useMemo(() => {
    const t = busca.trim().toLowerCase();
    return galeria.filter((f) => {
      if (ocultarMaster && f.master_url) return false;
      if (soSemVinculo && f.usos !== 0) return false;
      if (!t) return true;
      if ((f.nome ?? '').toLowerCase().includes(t)) return true;
      // O nome do arquivo entra na busca porque virou rotulo visivel (ver `rotulo`): sem isso a
      // pessoa le "10K_CLORO..." no card, digita CLORO e a foto some — a tela mostra o que a busca
      // nao encontra, que e o pior jeito de uma lista falhar.
      if (f.foto_url.toLowerCase().includes(t)) return true;
      return f.produtos.some(
        (p) => (p.codigo_interno ?? '').toLowerCase().includes(t) || p.descricao.toLowerCase().includes(t),
      );
    });
  }, [galeria, busca, ocultarMaster, soSemVinculo]);

  const semProduto = useMemo(() => filtrados.filter((f) => f.usos === 0).length, [filtrados]);

  // So conta (e so apaga) o que esta MARCADO e VISIVEL: uma marcada que o filtro escondeu nao pode
  // ser apagada sem que ele a veja.
  const marcadasVisiveis = useMemo(
    () => filtrados.filter((f) => selecionadas.has(f.foto_url)),
    [filtrados, selecionadas],
  );
  const todasMarcadas = filtrados.length > 0 && marcadasVisiveis.length === filtrados.length;

  function alternarSelecao(url: string) {
    setSelecionadas((prev) => {
      const n = new Set(prev);
      if (n.has(url)) n.delete(url);
      else n.add(url);
      return n;
    });
  }

  function alternarTodas() {
    setSelecionadas((prev) => {
      const n = new Set(prev);
      if (todasMarcadas) filtrados.forEach((f) => n.delete(f.foto_url));
      else filtrados.forEach((f) => n.add(f.foto_url));
      return n;
    });
  }

  // Apaga as marcadas uma por uma, pela mesma rota do "Excluir" do menu. Em fila, nao em paralelo:
  // cada exclusao mexe em produtos e no disco, e o contador mostra onde esta.
  async function apagarSelecionadas() {
    const alvo = marcadasVisiveis;
    if (!alvo.length) return;
    const comProduto = alvo.filter((f) => f.usos > 0);
    const produtosAfetados = comProduto.reduce((s, f) => s + f.usos, 0);
    const aviso = comProduto.length
      ? `\n\n${comProduto.length} delas estao em uso por ${produtosAfetados} produto(s), que vao ficar SEM foto.`
      : '\n\nNenhuma delas e usada por produto.';
    const ok = await dialog.confirmar(
      `Apagar ${alvo.length} foto(s) do acervo? O arquivo e apagado de vez.${aviso}`,
      { titulo: 'Apagar fotos selecionadas', perigoso: true, textoConfirmar: `Apagar ${alvo.length} foto(s)` },
    );
    if (!ok) return;
    const falhas: string[] = [];
    setApagandoLote({ feitas: 0, total: alvo.length });
    for (let i = 0; i < alvo.length; i++) {
      const f = alvo[i]!;
      try {
        await api.post('/api/produtos/foto-excluir', { foto_url: f.foto_url });
        setGaleria((prev) => prev.filter((g) => g.foto_url !== f.foto_url));
        setSelecionadas((prev) => {
          const n = new Set(prev);
          n.delete(f.foto_url);
          return n;
        });
        onFotoExcluida?.(f.foto_url);
      } catch (e: any) {
        falhas.push(`${arquivoDe(f.foto_url)}: ${e?.message ?? 'falhou'}`);
      }
      setApagandoLote({ feitas: i + 1, total: alvo.length });
    }
    setApagandoLote(null);
    if (falhas.length) {
      await dialog.alerta(
        `${alvo.length - falhas.length} de ${alvo.length} apagada(s). Nao consegui apagar:\n\n${falhas.join('\n')}`,
        { titulo: 'Apagar fotos selecionadas', tipo: 'aviso' },
      );
    } else {
      dialog.toast(`${alvo.length} foto(s) apagada(s)`, { tipo: 'sucesso' });
    }
  }

  // Nome a exibir: o nome proprio, ou a descricao do 1o produto como referencia.
  // Nome do ARQUIVO, sem pasta nem extensao. Vale como rotulo de ultimo recurso porque as sobras
  // antigas do disco tem nome descritivo ("10K_CLORO_HIDROSAN_PENTA.png") — melhor que "(sem nome)".
  function arquivoDe(fotoUrl: string): string {
    return (fotoUrl.split('/').pop() ?? '').replace(/\.[a-z0-9]+$/i, '');
  }

  function rotulo(f: GaleriaFoto): string {
    return f.nome || f.produtos[0]?.descricao || arquivoDe(f.foto_url) || '(sem nome)';
  }

  // Renomear = trocar o NOME DO ARQUIVO (04/11/2026, decisao do Ruan: um nome so por foto). O
  // servidor renomeia derivada + master e leva o endereco novo a todo lugar que guardava o antigo.
  // O "nome da foto" antigo (foto_metadata.nome) nao e' mais editado; quem ja tinha segue mostrando.
  function abrirEdicao(f: GaleriaFoto) {
    setEditandoUrl(f.foto_url);
    setNomeEdit(arquivoDe(f.foto_url));
  }

  async function salvarNome(f: GaleriaFoto) {
    const nome = nomeEdit.trim();
    if (!nome) {
      dialog.toast('O nome do arquivo nao pode ficar vazio', { tipo: 'erro' });
      return;
    }
    setSalvandoNome(true);
    try {
      const r = await api.post<{ foto_url: string; master_url: string | null; produtos_atualizados?: number; inalterado?: boolean }>(
        '/api/produtos/foto-renomear',
        { foto_url: f.foto_url, nome },
      );
      if (!r.inalterado) {
        setGaleria((prev) => prev.map((g) => (
          g.foto_url === f.foto_url ? { ...g, foto_url: r.foto_url, master_url: r.master_url ?? g.master_url } : g
        )));
        setSelecionadas((prev) => {
          if (!prev.has(f.foto_url)) return prev;
          const s = new Set(prev);
          s.delete(f.foto_url);
          s.add(r.foto_url);
          return s;
        });
        dialog.toast(
          `Arquivo renomeado para ${arquivoDe(r.foto_url)}${r.produtos_atualizados ? ` — ${r.produtos_atualizados} produto(s) atualizados` : ''}`,
          { tipo: 'sucesso' },
        );
      }
      setEditandoUrl(null);
    } catch (e: any) {
      dialog.toast(e.message ?? 'Falha ao renomear o arquivo', { tipo: 'erro' });
    } finally {
      setSalvandoNome(false);
    }
  }

  async function excluirFoto(f: GaleriaFoto) {
    const nomes = f.produtos.map((p) => p.descricao).slice(0, 6).join(', ');
    const resto = f.produtos.length > 6 ? ` e mais ${f.produtos.length - 6}` : '';
    const ok = await dialog.confirmar(
      `Esta foto e usada por ${f.usos} produto(s): ${nomes}${resto}.\n\n` +
        `Excluir vai apagar o arquivo de vez e deixar esses produtos SEM foto. Continuar?`,
      { titulo: 'Excluir foto do acervo', perigoso: true, textoConfirmar: 'Excluir foto' },
    );
    if (!ok) return;
    setExcluindo(f.foto_url);
    try {
      const r = await api.post<{ produtos_afetados: number }>('/api/produtos/foto-excluir', { foto_url: f.foto_url });
      dialog.toast(`Foto excluida — ${r.produtos_afetados} produto(s) ficaram sem foto`, { tipo: 'sucesso' });
      setGaleria((prev) => prev.filter((g) => g.foto_url !== f.foto_url));
      onFotoExcluida?.(f.foto_url);
    } catch (e: any) {
      dialog.toast(e.message ?? 'Falha ao excluir a foto', { tipo: 'erro' });
    } finally {
      setExcluindo(null);
    }
  }

  // Substituir o arquivo de uma foto: abre o seletor de arquivo. O upload e a
  // troca acontecem em aoEscolherArquivoSubstituir (quando o arquivo e escolhido).
  function pedirSubstituir(f: GaleriaFoto) {
    fotoSubstituirRef.current = f;
    inputSubstituirRef.current?.click();
  }

  // Mede as dimensoes (px) e o peso (bytes) de uma foto, no proprio navegador.
  async function medirFoto(url: string): Promise<MedidaFoto> {
    const [dim, bytes] = await Promise.all([
      new Promise<{ largura: number; altura: number }>((resolve) => {
        const img = new Image();
        img.onload = () => resolve({ largura: img.naturalWidth, altura: img.naturalHeight });
        img.onerror = () => resolve({ largura: 0, altura: 0 });
        img.src = url;
      }),
      fetch(url).then((r) => r.blob()).then((b) => b.size).catch(() => 0),
    ]);
    return { largura: dim.largura, altura: dim.altura, bytes };
  }

  async function aoEscolherArquivoSubstituir(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = ''; // permite reescolher o mesmo arquivo depois
    const alvo = fotoSubstituirRef.current;
    fotoSubstituirRef.current = null;
    if (!file || !alvo) return;

    setSubstituindo(alvo.foto_url);
    try {
      // Sobe a foto nova como PROVISORIA (04/11/2026, mesma porta da ficha): ela so entra no acervo
      // no Confirmar, com o nome do arquivo original; cancelar a comparacao descarta. Arquivo com o
      // mesmo nome da foto atual (`alvo`) troca no mesmo endereco em vez de acusar nome repetido.
      const fd = new FormData();
      fd.append('foto', file);
      fd.append('nome', file.name);
      fd.append('intacto', '1');
      fd.append('alvo', alvo.foto_url);
      const token = localStorage.getItem('fiodecobre.token');
      const resUp = await fetch('/api/upload/foto-ficha', {
        method: 'POST',
        body: fd,
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!resUp.ok) {
        const d = (await resUp.json().catch(() => ({}))) as { erro?: string; motivos?: string[] };
        throw new Error(d.motivos?.length ? d.motivos.join(' ') : d.erro || 'falha ao enviar a imagem');
      }
      const { url: urlNova, nome: nomeNovo } = (await resUp.json()) as { url: string; nome: string };

      // Mede as duas e abre o modal de comparacao (atual x nova).
      const [atual, nova] = await Promise.all([medirFoto(alvo.foto_url), medirFoto(urlNova)]);
      setModalSubst({ foto: alvo, urlNova, nomeNovo, atual, nova });
    } catch (err: any) {
      dialog.toast(err.message ?? 'Falha ao preparar a substituição', { tipo: 'erro' });
    } finally {
      setSubstituindo(null);
    }
  }

  // Aplicar Master "pronto": o usuario escolhe uma foto JA recortada (PNG
  // transparente — ex: a saida do FotoTool local) e ela vira o master, sem
  // rodar a IA no navegador (que trava em maquina fraca). Endpoint master-cliente.
  function pedirAplicarMaster(f: GaleriaFoto) {
    fotoMasterRef.current = f;
    inputMasterRef.current?.click();
  }

  // Detecta se a imagem tem transparencia (algum pixel com alfa < 250).
  function imagemTemTransparencia(file: File): Promise<boolean> {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        const lado = 90;
        const cv = document.createElement('canvas');
        cv.width = lado;
        cv.height = lado;
        const ctx = cv.getContext('2d');
        if (!ctx) { resolve(true); return; } // na duvida, deixa passar
        ctx.drawImage(img, 0, 0, lado, lado);
        try {
          const d = ctx.getImageData(0, 0, lado, lado).data;
          for (let i = 3; i < d.length; i += 4) if (d[i]! < 250) { resolve(true); return; }
          resolve(false);
        } catch {
          resolve(true);
        }
      };
      img.onerror = () => resolve(true);
      img.src = URL.createObjectURL(file);
    });
  }

  async function aoEscolherArquivoMaster(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = '';
    const alvo = fotoMasterRef.current;
    fotoMasterRef.current = null;
    if (!file || !alvo) return;

    if (!(await imagemTemTransparencia(file))) {
      const ok = await dialog.confirmar(
        'Essa foto parece estar COM fundo (nao e transparente). O Master fica bom quando a foto ja vem recortada — PNG transparente, ex: a saida do FotoTool. Aplicar assim mesmo?',
        { perigoso: true },
      );
      if (!ok) return;
    }

    dialog.toast('Aplicando Master...');
    try {
      const fd = new FormData();
      fd.append('imagem', file);
      fd.append('foto_url', alvo.foto_url);
      const token = localStorage.getItem('fiodecobre.token');
      const resp = await fetch('/api/upload/master-cliente', {
        method: 'POST',
        body: fd,
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!resp.ok) {
        const d = await resp.json().catch(() => ({}));
        throw new Error((d as any).erro || `HTTP ${resp.status}`);
      }
      const r = (await resp.json()) as { master_url: string; avaliacao: { veredito?: string } | null };
      const completo = r.avaliacao?.veredito === 'completo';
      setGaleria((prev) => prev.map((g) => (g.foto_url === alvo.foto_url ? { ...g, master_url: r.master_url, master_completo: completo } : g)));
      dialog.toast(
        completo ? 'Master aplicado (completo)' : 'Master aplicado (parcial — resolucao abaixo de 2000px)',
        { tipo: 'sucesso' },
      );
    } catch (e: any) {
      await dialog.alerta(
        `Nao consegui aplicar o Master.\n\nMotivo: ${e?.message ?? 'erro desconhecido'}`,
        { titulo: 'Falha ao aplicar Master', tipo: 'erro' },
      );
    }
  }

  // Cancelou a comparacao: a foto nova era provisoria e nao entra no acervo.
  function cancelarSubstituicao() {
    if (aplicandoSubst || !modalSubst) return;
    api.post('/api/upload/foto-ficha/descartar', { url: modalSubst.urlNova }).catch(() => {});
    setModalSubst(null);
  }

  // Confirmado no modal: aplica a substituicao em todos os produtos vinculados.
  async function confirmarSubstituicao() {
    if (!modalSubst) return;
    setAplicandoSubst(true);
    try {
      const r = await api.post<{ produtos_atualizados: number }>('/api/produtos/foto-substituir', {
        url_antiga: modalSubst.foto.foto_url,
        url_nova: modalSubst.urlNova,
      });
      dialog.toast(`Foto substituída — ${r.produtos_atualizados} produto(s) atualizados`, { tipo: 'sucesso' });
      setModalSubst(null);
      carregar();
    } catch (err: any) {
      dialog.toast(err.message ?? 'Falha ao substituir a foto', { tipo: 'erro' });
    } finally {
      setAplicandoSubst(false);
    }
  }

  // Uma coluna do comparativo de substituicao: foto + resolucao (px) + peso (KB).
  function colunaFoto(rotulo: string, url: string, m: MedidaFoto, destaque: boolean) {
    return (
      <div
        style={{
          flex: 1,
          minWidth: 0,
          border: `1px solid ${destaque ? 'var(--primary)' : 'var(--panel-border, #e5e7eb)'}`,
          borderRadius: 8,
          padding: 10,
          textAlign: 'center',
          background: 'var(--input-bg, #fff)',
        }}
      >
        <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 6 }}>{rotulo}</div>
        <img
          src={url}
          alt={rotulo}
          style={{
            width: '100%',
            aspectRatio: '1',
            objectFit: 'contain',
            background: '#fff',
            borderRadius: 4,
            border: '1px solid var(--panel-border, #e5e7eb)',
          }}
        />
        <div style={{ marginTop: 8, fontSize: 13 }}>
          <div style={{ fontWeight: 700 }}>
            {m.largura > 0 && m.altura > 0 ? `${m.largura} × ${m.altura} px` : '—'}
          </div>
          <div style={{ color: 'var(--text-muted, #6b7280)', marginTop: 2 }}>{kb(m.bytes)}</div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '70vh', gap: 10 }} className="text-sm">
      <p className="text-sm text-gray-600" style={{ flexShrink: 0 }}>
        Pesquise pelo <strong>nome do produto</strong>, <strong>código</strong> ou nome da foto.
        {onEscolher ? (
          <> Clique numa foto pra usar a <strong>mesma imagem</strong> aqui — sem precisar enviar um arquivo novo.</>
        ) : (
          <> Renomeie, veja os produtos vinculados, substitua o arquivo ou exclua do acervo.</>
        )}
      </p>

      <div style={{ position: 'relative', flexShrink: 0 }}>
        <Search size={16} style={{ position: 'absolute', left: 10, top: 11, color: 'var(--text-muted, #6b7280)' }} />
        <input
          type="text"
          autoFocus
          placeholder="Filtrar por nome da foto, codigo ou descricao..."
          value={busca}
          onChange={(e) => setBusca(e.target.value)}
          style={{
            width: '100%',
            padding: '9px 10px 9px 34px',
            border: '1px solid var(--input-border, #d1d5db)',
            borderRadius: 6,
            background: 'var(--input-bg)',
            color: 'var(--text-color)',
            fontSize: 14,
          }}
        />
      </div>

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
          fontSize: 12,
          color: 'var(--text-muted, #6b7280)',
          flexShrink: 0,
        }}
      >
        <span>
          {carregando
            ? 'Carregando acervo...'
            : `${filtrados.length} foto${filtrados.length === 1 ? '' : 's'}${busca.trim() || ocultarMaster || soSemVinculo ? ' encontrada(s)' : ' no acervo'}`}
          {/* Quantas do recorte em tela nao tem dono — o mesmo criterio do selo SEM PRODUTO. */}
          {!carregando && semProduto > 0 && (
            <span style={{ color: '#c2410c', fontWeight: 600 }}> · {semProduto} sem produto</span>
          )}
        </span>
        {!onEscolher && !carregando && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            {marcadasVisiveis.length > 0 && (
              <button
                type="button"
                className="btn-primario"
                onClick={apagarSelecionadas}
                disabled={!!apagandoLote}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap', flex: '0 0 auto' }}
              >
                <Trash2 size={15} />
                {apagandoLote
                  ? `Apagando ${apagandoLote.feitas} de ${apagandoLote.total}...`
                  : `Apagar selecionadas (${marcadasVisiveis.length})`}
              </button>
            )}
            <label
              title="Marca todas as fotos que aparecem agora na lista"
              style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', userSelect: 'none' }}
            >
              <input
                type="checkbox"
                checked={todasMarcadas}
                onChange={alternarTodas}
                disabled={!!apagandoLote || filtrados.length === 0}
              />
              Selecionar Todos
            </label>
            <label
              title="Mostra so fotos que nenhum produto usa"
              style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', userSelect: 'none' }}
            >
              <input
                type="checkbox"
                checked={soSemVinculo}
                onChange={(e) => setSoSemVinculo(e.target.checked)}
              />
              Fotos sem Vinculo
            </label>
            <label
              title="Mostra so fotos que ainda nao tem master (recorte transparente)"
              style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', userSelect: 'none' }}
            >
              <input
                type="checkbox"
                checked={ocultarMaster}
                onChange={(e) => setOcultarMaster(e.target.checked)}
              />
              Ocultar Master
            </label>
            {/* Porta de entrada do acervo. Botao SOLIDO de proposito: controle de
                grade nasce com fundo, nao como link discreto. */}
            <button
              type="button"
              className="btn-primario"
              onClick={() => setEnviarAberto(true)}
              title="Enviar fotos novas pro acervo (padrao Master)"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap', flex: '0 0 auto' }}
            >
              <Upload size={15} /> Enviar fotos
            </button>
          </div>
        )}
      </div>

      {/* Grade rolavel — ocupa todo o espaco restante do modal */}
      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', paddingRight: 4 }}>
        {carregando ? (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted, #6b7280)' }}>Carregando...</div>
        ) : filtrados.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted, #6b7280)' }}>
            {galeria.length === 0 ? 'Nenhuma foto no acervo ainda.' : 'Nenhuma foto encontrada pra essa busca.'}
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(170px, 1fr))', gap: 12 }}>
            {filtrados.map((f) => {
              const editando = editandoUrl === f.foto_url;
              return (
                <div
                  key={f.foto_url}
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 4,
                    padding: 6,
                    border: '1px solid var(--panel-border, #e5e7eb)',
                    borderRadius: 8,
                    background: 'var(--input-bg, #fff)',
                    opacity: excluindo === f.foto_url || substituindo === f.foto_url ? 0.5 : 1,
                  }}
                >
                  {/* Foto — clicar seleciona */}
                  <div style={{ position: 'relative' }}>
                    <img
                      src={f.foto_url}
                      alt=""
                      loading="lazy"
                      onClick={onEscolher ? () => onEscolher(f.foto_url) : () => setZoom(f.foto_url)}
                      title={onEscolher ? 'Usar esta foto' : 'Clique para ampliar'}
                      className="hover:opacity-80 transition"
                      style={{
                        width: '100%',
                        aspectRatio: '1',
                        objectFit: 'contain',
                        background: '#fff',
                        borderRadius: 4,
                        border: '1px solid var(--panel-border, #e5e7eb)',
                        cursor: 'pointer',
                      }}
                    />
                    {f.master_completo ? (
                      <span
                        title="Foto no padrao Master — alta resolucao, quadrada e fundo transparente"
                        style={{
                          position: 'absolute',
                          top: 4,
                          left: 4,
                          background: 'var(--accent)',
                          color: 'var(--accent-fg)',
                          fontSize: 9,
                          fontWeight: 800,
                          letterSpacing: 0.5,
                          padding: '2px 6px',
                          borderRadius: 999,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 3,
                        }}
                      >
                        <BadgeCheck size={10} /> MASTER
                      </span>
                    ) : f.master_url ? (
                      <span
                        title="Esta foto ja tem versao sem fundo (master parcial)"
                        style={{
                          position: 'absolute',
                          top: 4,
                          left: 4,
                          background: '#16a34a',
                          color: '#fff',
                          fontSize: 9,
                          fontWeight: 800,
                          letterSpacing: 0.5,
                          padding: '2px 6px',
                          borderRadius: 999,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 3,
                        }}
                      >
                        <Scissors size={9} /> SEM FUNDO
                      </span>
                    ) : null}
                    {f.usos === 0 && (
                      // Laranja = atencao (o vermelho e reservado a erro): a foto nao esta errada,
                      // esta pendente de vinculo. Fica embaixo pra nao brigar com o selo do Master,
                      // que ocupa o topo — os dois convivem na mesma foto.
                      <span
                        title="Nenhum produto usa esta foto — pode ser uma foto promovida esperando vinculo, ou sobra antiga"
                        style={{
                          position: 'absolute',
                          bottom: 4,
                          left: 4,
                          background: '#f97316',
                          color: '#fff',
                          fontSize: 9,
                          fontWeight: 800,
                          letterSpacing: 0.5,
                          padding: '2px 6px',
                          borderRadius: 999,
                        }}
                      >
                        SEM PRODUTO
                      </span>
                    )}
                    {!onEscolher && (
                      <button
                        type="button"
                        data-menu-foto
                        onClick={(e) => toggleMenu(e, f)}
                        title="Ações da foto"
                        style={{
                          position: 'absolute',
                          top: 4,
                          right: 4,
                          background: 'rgba(255,255,255,0.92)',
                          border: '1px solid var(--panel-border, #e5e7eb)',
                          borderRadius: 6,
                          cursor: 'pointer',
                          padding: 3,
                          display: 'flex',
                          color: '#374151',
                        }}
                      >
                        <MoreVertical size={16} />
                      </button>
                    )}
                    {!onEscolher && (
                      // Canto de baixo a direita: o de cima tem o selo e o menu, o de baixo a esquerda
                      // o SEM PRODUTO.
                      <input
                        type="checkbox"
                        checked={selecionadas.has(f.foto_url)}
                        onChange={() => alternarSelecao(f.foto_url)}
                        disabled={!!apagandoLote}
                        title="Marcar pra apagar"
                        aria-label={`Marcar ${rotulo(f)}`}
                        style={{ position: 'absolute', bottom: 6, right: 6, width: 18, height: 18, cursor: 'pointer', margin: 0 }}
                      />
                    )}
                  </div>

                  <span
                    style={{
                      fontSize: 14,
                      lineHeight: 1.3,
                      fontWeight: f.nome ? 600 : 400,
                      fontStyle: f.nome ? 'normal' : 'italic',
                      color: 'var(--text-color)',
                      overflow: 'hidden',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                    }}
                  >
                    {rotulo(f)}
                  </span>

                  {f.produtos[0]?.codigo_interno && (
                    <div style={{ fontSize: 11, color: 'var(--text-muted, #6b7280)', fontWeight: 700 }}>
                      {f.produtos[0].codigo_interno}
                    </div>
                  )}

                  {/* Nome do arquivo no disco, igual ao da ficha do produto (preto 12px, pedido do Ruan
                      04/11/2026): da' pra comparar visualmente as copias e pesquisar pelo nome. O lapis
                      (e o "Renomear arquivo" do menu) renomeia o ARQUIVO. */}
                  {editando ? (
                    <div style={{ display: 'flex', gap: 3, alignItems: 'center' }}>
                      <input
                        type="text"
                        autoFocus
                        value={nomeEdit}
                        onChange={(e) => setNomeEdit(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') salvarNome(f);
                          if (e.key === 'Escape') setEditandoUrl(null);
                        }}
                        placeholder="Nome do arquivo"
                        disabled={salvandoNome}
                        style={{
                          flex: 1,
                          minWidth: 0,
                          padding: '3px 5px',
                          fontSize: 12,
                          textTransform: 'none',
                          border: '1px solid var(--input-border, #d1d5db)',
                          borderRadius: 4,
                          background: 'var(--input-bg)',
                          color: 'var(--text-color)',
                        }}
                      />
                      <button
                        type="button"
                        onClick={() => salvarNome(f)}
                        disabled={salvandoNome}
                        title="Salvar o nome do arquivo"
                        style={{ background: 'transparent', border: 'none', color: '#16a34a', cursor: 'pointer', padding: 2 }}
                      >
                        <Check size={15} />
                      </button>
                      <button
                        type="button"
                        onClick={() => setEditandoUrl(null)}
                        disabled={salvandoNome}
                        title="Cancelar"
                        style={{ background: 'transparent', border: 'none', color: '#6b7280', cursor: 'pointer', padding: 2 }}
                      >
                        <X size={15} />
                      </button>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', gap: 3, alignItems: 'flex-start' }}>
                      <div
                        title={f.foto_url}
                        style={{ flex: 1, minWidth: 0, fontSize: 12, color: '#000', overflowWrap: 'anywhere', lineHeight: 1.3 }}
                      >
                        {arquivoDe(f.foto_url)}
                      </div>
                      <button
                        type="button"
                        onClick={() => abrirEdicao(f)}
                        title="Renomear o arquivo"
                        aria-label={`Renomear o arquivo ${arquivoDe(f.foto_url)}`}
                        style={{ background: 'transparent', border: 'none', color: 'var(--primary)', cursor: 'pointer', padding: 0, flexShrink: 0 }}
                      >
                        <Pencil size={13} />
                      </button>
                    </div>
                  )}

                  {/* Rodape: tamanho + usos + excluir */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: 'var(--text-muted, #6b7280)' }}>
                    <span>{kb(f.bytes)}</span>
                    <span>·</span>
                    <button
                      type="button"
                      data-foto-usos
                      onClick={(e) => toggleLista(e, f)}
                      title="Ver os produtos que usam esta foto"
                      style={{
                        flex: 1,
                        textAlign: 'left',
                        background: 'transparent',
                        border: 'none',
                        padding: 0,
                        fontSize: 11,
                        color: 'var(--primary)',
                        cursor: 'pointer',
                        textDecoration: 'underline',
                        textDecorationStyle: 'dotted',
                      }}
                    >
                      {f.usos} produto{f.usos === 1 ? '' : 's'}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {onCancelar && (
        <div style={{ flexShrink: 0 }}>
          <button
            type="button"
            onClick={onCancelar}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-gray-100 text-gray-700 hover:bg-gray-200"
          >
            <ArrowLeft size={16} /> Voltar
          </button>
        </div>
      )}

      {/* Input escondido — seleciona o arquivo novo pra substituir uma foto. */}
      <input
        ref={inputSubstituirRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        onChange={aoEscolherArquivoSubstituir}
        style={{ display: 'none' }}
      />
      {/* Input escondido — seleciona uma foto JA sem fundo pra aplicar como Master (sem IA). */}
      <input
        ref={inputMasterRef}
        type="file"
        accept="image/png,image/webp"
        onChange={aoEscolherArquivoMaster}
        style={{ display: 'none' }}
      />

      {/* Modal: comparativo foto atual x foto nova antes de confirmar a substituicao. */}
      {modalSubst && (
        <Modal
          aberto
          onFechar={cancelarSubstituicao}
          titulo="Substituir foto"
          largura={620}
          rodape={
            <>
              <button
                type="button"
                className="btn-secundario"
                onClick={cancelarSubstituicao}
                disabled={aplicandoSubst}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="btn-primario"
                onClick={confirmarSubstituicao}
                disabled={aplicandoSubst}
              >
                {aplicandoSubst
                  ? 'Substituindo...'
                  : `Substituir em ${modalSubst.foto.usos} produto${modalSubst.foto.usos === 1 ? '' : 's'}`}
              </button>
            </>
          }
        >
          <p style={{ fontSize: 13, color: 'var(--text-muted, #6b7280)', marginBottom: 14 }}>
            A foto nova vai substituir a atual em <strong>{modalSubst.foto.usos} produto
            {modalSubst.foto.usos === 1 ? '' : 's'}</strong> de uma vez. Confira abaixo antes de confirmar.
            <br />
            {modalSubst.nomeNovo === arquivoDe(modalSubst.foto.foto_url)
              ? <>Mesmo nome de arquivo: a troca acontece no próprio <strong>{modalSubst.nomeNovo}</strong>.</>
              : <>No acervo ela entra como <strong>{modalSubst.nomeNovo}</strong>, e a atual é apagada.</>}
          </p>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            {colunaFoto('Foto atual', modalSubst.foto.foto_url, modalSubst.atual, false)}
            <div style={{ fontSize: 24, color: 'var(--text-muted, #9ca3af)', flexShrink: 0 }}>→</div>
            {colunaFoto('Foto nova', modalSubst.urlNova, modalSubst.nova, true)}
          </div>
        </Modal>
      )}

      {/* Popover: produtos que usam a foto clicada. Posicao fixed (modal tem scroll). */}
      {listaAberta && (
        <div
          ref={popoverRef}
          style={{
            position: 'fixed',
            top: listaAberta.top,
            left: listaAberta.left,
            width: 320,
            maxHeight: 320,
            overflowY: 'auto',
            zIndex: 1200,
            background: 'var(--modal-bg, var(--panel-bg, #fff))',
            color: 'var(--text-color)',
            border: '1px solid var(--panel-border, #d1d5db)',
            borderRadius: 8,
            boxShadow: '0 6px 24px rgba(0,0,0,0.18)',
            padding: 8,
            fontSize: 12,
          }}
        >
          <div style={{ fontWeight: 700, marginBottom: 6, paddingBottom: 4, borderBottom: '1px solid var(--panel-border, #e5e7eb)' }}>
            {listaAberta.foto.usos} produto{listaAberta.foto.usos === 1 ? '' : 's'} com esta foto
          </div>
          {listaAberta.foto.produtos.map((p) => (
            <div key={p.id} style={{ display: 'flex', gap: 6, padding: '3px 2px', lineHeight: 1.3 }}>
              <span style={{ fontWeight: 700, color: 'var(--text-muted, #6b7280)', flexShrink: 0 }}>{p.codigo_interno || '—'}</span>
              <span style={{ flex: 1, minWidth: 0 }}>{p.descricao}</span>
            </div>
          ))}
        </div>
      )}

      {/* Menu "..." de acoes da foto. Posicao fixed (a grade tem scroll). */}
      {menuFoto && (
        <div
          ref={menuRef}
          style={{
            position: 'fixed',
            top: menuFoto.top,
            left: menuFoto.left,
            width: 220,
            zIndex: 1250,
            background: 'var(--modal-bg, var(--panel-bg, #fff))',
            color: 'var(--text-color)',
            border: '1px solid var(--panel-border, #d1d5db)',
            borderRadius: 8,
            boxShadow: '0 6px 24px rgba(0,0,0,0.18)',
            padding: 4,
          }}
        >
          <ItemMenu
            icone={<Scissors size={15} />}
            cor="#9333ea"
            texto={menuFoto.foto.master_url ? 'Ver foto sem fundo' : 'Retirar fundo'}
            onClick={() => {
              setRetirarFundoFoto(menuFoto.foto);
              setMenuFoto(null);
            }}
          />
          <ItemMenu
            icone={<BadgeCheck size={15} />}
            cor="#16a34a"
            texto="Aplicar Master (foto pronta)"
            onClick={() => {
              pedirAplicarMaster(menuFoto.foto);
              setMenuFoto(null);
            }}
          />
          <ItemMenu
            icone={<CopyPlus size={15} />}
            cor="#ea580c"
            texto="Aplicar em produtos"
            onClick={() => {
              setAplicarEmProdutos(menuFoto.foto);
              setMenuFoto(null);
            }}
          />
          <ItemMenu
            icone={<Upload size={15} />}
            cor="var(--primary)"
            texto="Substituir foto"
            onClick={() => {
              const f = menuFoto.foto;
              setMenuFoto(null);
              pedirSubstituir(f);
            }}
          />
          <ItemMenu
            icone={<Pencil size={15} />}
            cor="var(--primary)"
            texto="Renomear arquivo"
            onClick={() => {
              const f = menuFoto.foto;
              setMenuFoto(null);
              abrirEdicao(f);
            }}
          />
          <ItemMenu
            icone={<Trash2 size={15} />}
            cor="#dc2626"
            texto="Excluir foto"
            onClick={() => {
              const f = menuFoto.foto;
              setMenuFoto(null);
              excluirFoto(f);
            }}
          />
        </div>
      )}

      {/* Retirar fundo / ver sem fundo — acionado pelo menu (auto-abre ao montar). */}
      {retirarFundoFoto && (
        <BotaoRetirarFundo
          key={retirarFundoFoto.foto_url}
          variante="controlado"
          fotoUrl={retirarFundoFoto.foto_url}
          masterUrl={retirarFundoFoto.master_url}
          onGerado={(masterUrl, completo) =>
            setGaleria((prev) =>
              prev.map((g) =>
                g.foto_url === retirarFundoFoto.foto_url
                  ? { ...g, master_url: masterUrl, master_completo: completo }
                  : g,
              ),
            )
          }
          onFecharControle={() => setRetirarFundoFoto(null)}
        />
      )}

      {/* Aplicar uma foto em varios produtos de uma vez. */}
      {aplicarEmProdutos && (
        <ModalAplicarFotoProdutos
          fotoUrl={aplicarEmProdutos.foto_url}
          fotoNome={aplicarEmProdutos.nome}
          onFechar={() => setAplicarEmProdutos(null)}
          onAplicado={() => {
            setAplicarEmProdutos(null);
            carregar();
          }}
        />
      )}

      {/* Enviar fotos avulsas pro acervo (padrao Master). So no modo gestao. */}
      <ModalEnviarFotosAcervo
        aberto={enviarAberto}
        onFechar={() => setEnviarAberto(false)}
        onEntraram={carregar}
      />

      {/* Zoom: amplia a foto clicada em tela cheia. Fecha no clique ou no Esc. */}
      {zoom && (
        <div
          onClick={() => setZoom(null)}
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 1300,
            background: 'rgba(0,0,0,0.85)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'zoom-out',
            padding: 24,
          }}
        >
          <img
            src={zoom}
            alt=""
            style={{ maxWidth: '94vw', maxHeight: '94vh', objectFit: 'contain', borderRadius: 6 }}
          />
          <button
            type="button"
            onClick={() => setZoom(null)}
            title="Fechar"
            style={{
              position: 'fixed',
              top: 16,
              right: 16,
              background: 'rgba(255,255,255,0.15)',
              border: 'none',
              borderRadius: 8,
              color: '#fff',
              cursor: 'pointer',
              padding: 8,
            }}
          >
            <X size={22} />
          </button>
        </div>
      )}
    </div>
  );
}
